import { useState, useEffect } from "react";
import PatientService, { LaravelPatient } from "../services/patientService";
import { Clinic } from "../types/Patient";

export interface PatientFormData {
  firstName: string;
  lastName: string;
  fatherName: string;
  motherName: string;
  birthDate: string;
  gender: "male" | "female";
  bloodType: string;
  phone: string;
  email: string;
  address: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;
  medicalNotes: string;
  allergies: string;
  chronicConditions: string;
  insuranceProvider: string;
  insuranceNumber: string;
  primaryDoctorId?: number;
  clinicId?: number;
}

interface Doctor {
  id: number;
  name: string;
  specialty?: string;
}

// Define your User type (or import if you have it elsewhere)
interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  clinic_id?: number;
  roles: string[];
}

// usePatientForm.ts - Updated validation function

export const validatePatientForm = (
  data: PatientFormData
): Record<string, string> => {
  const errors: Record<string, string> = {};
  
  // Required fields
  if (!data.firstName.trim()) {
    errors.firstName = "First name is required";
  }
  
  if (!data.lastName.trim()) {
    errors.lastName = "Last name is required";
  }
  
  if (!data.birthDate) {
    errors.birthDate = "Birth date is required";
  }
  
  if (!data.phone.trim()) {
    errors.phone = "Phone number is required";
  }
  
  if (!data.address.trim()) {
    errors.address = "Address is required";
  }
  
  // ✅ REMOVED: Emergency contact required validations
  // Emergency contact fields are now optional
  
  // Email validation (if provided)
  if (data.email && !/\S+@\S+\.\S+/.test(data.email)) {
    errors.email = "Please enter a valid email address";
  }
  
  // Phone validation
  if (data.phone && !/^\+?[\d\s\-\(\)]+$/.test(data.phone)) {
    errors.phone = "Please enter a valid phone number";
  }
  
  // ✅ UPDATED: Only validate emergency contact phone format if provided
  if (data.emergencyContactPhone && !/^\+?[\d\s\-\(\)]+$/.test(data.emergencyContactPhone)) {
    errors.emergencyContactPhone = "Please enter a valid emergency contact phone";
  }
  
  // Birth date validation
  if (data.birthDate) {
    const birthDate = new Date(data.birthDate);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    
    if (birthDate > today) {
      errors.birthDate = "Birth date cannot be in the future";
    } else if (age > 120) {
      errors.birthDate = "Please enter a valid birth date";
    }
  }
  
  return errors;
};

export const usePatientForm = () => {
  const [formData, setFormData] = useState<PatientFormData>({
    firstName: "",
    lastName: "",
    fatherName: "",
    motherName: "",
    birthDate: "",
    gender: "male",
    bloodType: "",
    phone: "",
    email: "",
    address: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    emergencyContactRelationship: "",
    medicalNotes: "",
    allergies: "",
    chronicConditions: "",
    insuranceProvider: "",
    insuranceNumber: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [createdPatient, setCreatedPatient] = useState<LaravelPatient | null>(null); // Add this line

  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [user, setUser] = useState<User | undefined>(undefined);

  useEffect(() => {
    const loadData = async () => {
      try {
        const doctorsList = await PatientService.getDoctors();
        setDoctors(doctorsList);

        // In a real app, replace mocks with your auth/context data and API call
        const mockUser: User = {
          id: 1,
          first_name: "John",
          last_name: "Doe",
          email: "john@example.com",
          clinic_id: 1,
          roles: ["doctor"],
        };
        setUser(mockUser);

        const mockClinics: Clinic[] = [
          {
            id: 1,
            name: "Caduceus Medical Center",
            address: "Beirut, Lebanon",
            phone: "+961-1-234567",
            is_active: true,
          },
        ];
        setClinics(mockClinics);

        if (mockUser.clinic_id && !formData.clinicId) {
          setFormData((prev) => ({
            ...prev,
            clinicId: mockUser.clinic_id,
          }));
        }
      } catch (error) {
        console.error("Failed to load data:", error);
        setDoctors([]);
        setClinics([]);
      }
    };
    loadData();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validatePatientForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return false;
    }
    setIsLoading(true);
    try {
      const patient = await PatientService.createPatient(formData); // Store the response
      setCreatedPatient(patient); // Save the created patient data
      setIsSuccess(true);
      resetForm();
      return true;
    } catch (err: any) {
      setErrors({
        submit: err.message || "Failed to register patient. Please try again.",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      fatherName: "",
      motherName: "",
      birthDate: "",
      gender: "male",
      bloodType: "",
      phone: "",
      email: "",
      address: "",
      emergencyContactName: "",
      emergencyContactPhone: "",
      emergencyContactRelationship: "",
      medicalNotes: "",
      allergies: "",
      chronicConditions: "",
      insuranceProvider: "",
      insuranceNumber: "",
      clinicId: user?.clinic_id,
    });
    setErrors({});
  };

  return {
    formData,
    setFormData,
    errors,
    isLoading,
    isSuccess,
    createdPatient, // Add this to the return
    doctors,
    clinics,
    user,
    handleInputChange,
    handleSubmit,
    resetForm,
    setIsSuccess,
    setIsLoading,
  };
};